import { ToastAlertProvider } from '@procore/toast-alert'
import { useFeatureFlag } from '@procore/web-sdk-feature-toggle'
import React from 'react'

import { CKTextEditor } from './CKTextEditor'
import { FeatureFlagProvider } from './Providers/FeatureFlagProvider'
import type { TextEditorProps } from './TextEditor.types'
import { TextEditorI18nProvider } from './TextEditorI18nProvider'
import { TinyMCETextEditor } from './TinyMCETextEditor'

const FLAG_NAME = 'pmqs-ck-editor-enabled'

/**
 * To ensure your Jest tests work with the CKEditor implementation, wrap your Jest configuration with `textEditorJestConfig`:
 *
 * @example
 *
 *   // Using Jest config file
 *
 *   const { textEditorJestConfig } = require('@procore/text-editor/jestConfig')
 *
 *   module.exports = textEditorJestConfig({
 *     // Your existing Jest config
 *   })
 *
 * @example
 *
 *   // Using Hammer
 *
 *   import { textEditorJestConfig } from '@procore/text-editor/jestConfig'
 *
 *   export default {
 *     testJest: (defaultConfig) =>
 *       textEditorJestConfig({
 *         ...defaultConfig,
 *         // Your existing Jest config
 *       }),
 *   }
 *
 * @example
 *
 *   // Using Core Scripts
 *
 *   const { textEditorJestConfig } = require('@procore/text-editor/jestConfig')
 *
 *   module.exports = () => ({
 *     jestOverride: (defaultConfig) =>
 *       textEditorJestConfig({
 *         ...defaultConfig,
 *         // Your existing Jest config
 *       }),
 *   })
 */
export function TextEditor(props: Readonly<TextEditorProps>) {
  return (
    <TextEditorI18nProvider>
      <ToastAlertProvider>
        <FeatureFlagProvider>
          <TextEditorInner {...props} />
        </FeatureFlagProvider>
      </ToastAlertProvider>
    </TextEditorI18nProvider>
  )
}

function TextEditorInner(props: Readonly<TextEditorProps>) {
  const { enforceCKEditor, ...editorProps } = props
  const flagValue = useFeatureFlag<boolean>(FLAG_NAME, false)
  const isCKEditorEnabled = enforceCKEditor ?? flagValue

  return isCKEditorEnabled ? (
    <CKTextEditor {...editorProps} />
  ) : (
    <TinyMCETextEditor {...editorProps} />
  )
}
